import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { getInterviewCalendar, completeInterview, sendInterviewReminder } from '../lib/api';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  CheckCircle,
  AlertTriangle,
  Phone,
  Mail,
  User,
  ArrowLeft,
  Loader2,
} from 'lucide-react';

const STATUS_COLORS = {
  scheduled: 'bg-blue-500',
  not_started: 'bg-amber-500',
  completed: 'bg-emerald-500',
  overdue: 'bg-red-500',
};

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  not_started: 'Not Started',
  completed: 'Completed',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function InterviewCalendarPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();

  const loadCalendar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getInterviewCalendar({ month, year });
      setCalendarData(res.data);
    } catch (err) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1));
    setSelectedDate(null);
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month, 1));
    setSelectedDate(null);
  };
  const goToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
  };

  const handleComplete = async (dealId) => {
    setActionLoading(dealId);
    try {
      await completeInterview(dealId);
      toast.success('Interview completed');
      loadCalendar();
    } catch { toast.error('Failed'); }
    finally { setActionLoading(null); }
  };

  const handleReminder = async (dealId) => {
    setActionLoading(dealId + '-remind');
    try {
      await sendInterviewReminder(dealId);
      toast.success('Reminder sent');
    } catch { toast.error('Failed to send reminder'); }
    finally { setActionLoading(null); }
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const prevMonthDays = new Date(year, month - 1, 0).getDate();

  const cells = [];
  // Previous month fill
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, inMonth: false, date: null });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, inMonth: true, date: dateKey });
  }
  // Next month fill
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, inMonth: false, date: null });
  }

  const byDate = calendarData?.by_date || {};
  const todayKey = new Date().toISOString().split('T')[0];
  const selectedEvents = selectedDate ? (byDate[selectedDate] || []) : [];

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <Layout>
      <Header />
      <main className="p-6 max-w-[1400px] mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/interviews')} data-testid="back-to-concierge">
              <ArrowLeft size={16} className="mr-1" /> Back to Concierge
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <CalendarIcon size={24} className="text-blue-600" />
              Interview Calendar
            </h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500"></span> Scheduled
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500 ml-2"></span> Not Started
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 ml-2"></span> Completed
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="calendar-grid">
            {/* Month navigation */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month">
                <ChevronLeft size={18} />
              </Button>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-slate-900">{monthName}</h2>
                <Button variant="outline" size="sm" onClick={goToday} className="text-xs">
                  Today
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month">
                <ChevronRight size={18} />
              </Button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-slate-100">
              {DAYS.map(d => (
                <div key={d} className="text-center py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {d}
                </div>
              ))}
            </div>

            {/* Calendar cells */}
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="animate-spin text-slate-400" size={32} />
              </div>
            ) : (
              <div className="grid grid-cols-7">
                {cells.map((cell, idx) => {
                  const events = cell.date ? (byDate[cell.date] || []) : [];
                  const isToday = cell.date === todayKey;
                  const isSelected = cell.date === selectedDate;
                  const hasEvents = events.length > 0;

                  return (
                    <div
                      key={idx}
                      onClick={() => cell.inMonth && cell.date && setSelectedDate(cell.date === selectedDate ? null : cell.date)}
                      className={`
                        min-h-[90px] p-2 border-b border-r border-slate-100 cursor-pointer transition-colors
                        ${!cell.inMonth ? 'bg-slate-50/50 text-slate-300' : 'hover:bg-blue-50/50'}
                        ${isSelected ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''}
                        ${isToday && !isSelected ? 'bg-amber-50/50' : ''}
                      `}
                      data-testid={cell.date ? `cal-cell-${cell.date}` : undefined}
                    >
                      <div className={`text-sm font-medium mb-1 ${isToday ? 'bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : ''}`}>
                        {cell.day}
                      </div>
                      {hasEvents && (
                        <div className="space-y-0.5">
                          {events.slice(0, 3).map((ev, i) => (
                            <div
                              key={i}
                              className={`text-[10px] px-1.5 py-0.5 rounded truncate text-white ${STATUS_COLORS[ev.interview_status] || 'bg-slate-400'}`}
                              title={`${ev.customer_name} - ${ev.deal_number}`}
                            >
                              {ev.customer_name?.split(' ')[0]} #{ev.deal_number}
                            </div>
                          ))}
                          {events.length > 3 && (
                            <div className="text-[10px] text-slate-500 pl-1">+{events.length - 3} more</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Side panel — selected day detail */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="day-detail-panel">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
              <h3 className="font-bold text-slate-900">
                {selectedDate
                  ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                  : 'Select a Date'}
              </h3>
              {selectedDate && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {selectedEvents.length} interview{selectedEvents.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
              {!selectedDate && (
                <p className="text-sm text-slate-400 text-center py-8">Click a date on the calendar to view interviews</p>
              )}
              {selectedDate && selectedEvents.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-8">No interviews scheduled for this date</p>
              )}
              {selectedEvents.map((ev) => {
                const time = new Date(ev.scheduled_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                return (
                  <div
                    key={ev.id}
                    className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer"
                    onClick={() => navigate(`/deal/${ev.id}`)}
                    data-testid={`event-${ev.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-slate-900">{ev.customer_name}</p>
                        <p className="text-xs text-slate-500">Deal #{ev.deal_number} &middot; {ev.lender}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full text-white font-medium ${STATUS_COLORS[ev.interview_status] || 'bg-slate-400'}`}>
                        {STATUS_LABELS[ev.interview_status] || ev.interview_status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-600 mb-3">
                      <span className="flex items-center gap-1"><Clock size={12} /> {time}</span>
                      <span className="flex items-center gap-1"><User size={12} /> {ev.finance_manager}</span>
                      <span className="flex items-center gap-1"><AlertTriangle size={12} /> {ev.deal_age}d old</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {ev.customer_phone && <span className="flex items-center gap-1"><Phone size={11} /> {ev.customer_phone}</span>}
                      {ev.customer_email && <span className="flex items-center gap-1"><Mail size={11} /> {ev.customer_email}</span>}
                    </div>
                    {ev.interview_status === 'scheduled' && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7"
                          onClick={() => handleReminder(ev.id)}
                          disabled={actionLoading === ev.id + '-remind'}
                          data-testid={`remind-${ev.id}`}
                        >
                          {actionLoading === ev.id + '-remind' ? <Loader2 size={12} className="animate-spin mr-1" /> : <Phone size={12} className="mr-1" />}
                          Send Reminder
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleComplete(ev.id)}
                          disabled={actionLoading === ev.id}
                          data-testid={`complete-${ev.id}`}
                        >
                          {actionLoading === ev.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle size={12} className="mr-1" />}
                          Complete
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}
